// AuthManager.tsx
'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fireAndForget } from '@sandstone/shared-utils';
import { useStytchMemberSession, useStytchMember, useStytchOrganization } from '@stytch/nextjs/b2b';
import { hasIntermediateSession, clearIntermediateSession } from './browser-utils';
import { convertMemberToUserProfile } from './auth-utils';
import { AuthContext } from './auth-context';
export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx)
        throw new Error('useAuth must be used inside <AuthProvider>');
    return ctx;
}
export function AuthManager({ children, initialUser, initialWorkspace, }) {
    const { session: stytchSession } = useStytchMemberSession();
    const { member: stytchMember, isInitialized } = useStytchMember();
    const { organization: stytchOrganization } = useStytchOrganization();
    const [userProfile, setUserProfile] = useState(initialUser ?? null);
    const [workspace, setWorkspace] = useState(initialWorkspace ?? null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [claims, setClaims] = useState(null);
    const refreshTimerRef = useRef(null);
    const refreshRetryRef = useRef(0);
    const lastRefreshRef = useRef(0);
    const lastWhoamiRef = useRef(0);
    const isRefreshingRef = useRef(false);
    const isWhoamiingRef = useRef(false);
    const hasFullSession = Boolean(stytchSession && stytchMember);
    const hasIntermediate = hasIntermediateSession();
    // Detect E2E bypass cookie (set by Autonoma browser automation).
    // Optimistic client-side signal to prevent login redirect before server validates.
    // Gated on NEXT_PUBLIC_E2E_BYPASS_ENABLED so it never activates in production builds.
    const hasE2EBypass = useMemo(() => {
        if (process.env.NEXT_PUBLIC_E2E_BYPASS_ENABLED !== 'true')
            return false;
        if (typeof document === 'undefined')
            return false;
        return document.cookie.split(';').some(c => c.trim().startsWith('__e2e_bypass='));
    }, []);
    // Detect dev-login cookie (set by /auth/dev-login page).
    // Non-production only — mirrors server-side isProductionEnv() logic.
    // Uses NEXT_PUBLIC_VERCEL_ENV (auto-exposed by Vercel) so preview deployments
    // (where NODE_ENV=production but VERCEL_ENV=preview) are correctly allowed.
    const hasDevLogin = useMemo(() => {
        if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'production')
            return false;
        if (!process.env.NEXT_PUBLIC_VERCEL_ENV && process.env.NODE_ENV === 'production')
            return false;
        if (typeof document === 'undefined')
            return false;
        return document.cookie.split(';').some(c => c.trim().startsWith('__dev_login='));
    }, []);
    const isAuthenticated = hasFullSession || hasIntermediate || Boolean(userProfile) || hasE2EBypass || hasDevLogin;
    const hasRefreshableSessionRef = useRef(false);
    useEffect(() => {
        hasRefreshableSessionRef.current = hasFullSession || Boolean(userProfile);
    }, [hasFullSession, userProfile]);
    // Ref to hold current refresh function - prevents stale closure in scheduleNext
    const refreshRef = useRef(() => Promise.resolve({ success: false, reason: 'not_initialized' }));
    const REFRESH_COOLDOWN_MS = 30 * 1000;
    const WHOAMI_COOLDOWN_MS = 2 * 60 * 1000;
    const clearRefreshTimer = useCallback(() => {
        if (refreshTimerRef.current) {
            window.clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
    }, []);
    const whoami = useCallback(async () => {
        const now = Date.now();
        if (isWhoamiingRef.current || now - lastWhoamiRef.current < WHOAMI_COOLDOWN_MS) {
            return { success: false, reason: 'rate_limited' };
        }
        isWhoamiingRef.current = true;
        lastWhoamiRef.current = now;
        try {
            // Removed cache manager usage - React Query handles all caching now
            const r = await fetch('/api/auth/whoami', {
                method: 'GET',
                credentials: 'include',
                headers: { 'Cache-Control': 'no-store' },
            });
            if (!r.ok)
                throw new Error(`whoami failed: ${r.status}`);
            const data = await r.json();
            const memberId = data.member?.id ?? data.session?.member_id ?? userProfile?.id ?? null;
            if (memberId) {
                const profile = (stytchMember && convertMemberToUserProfile(stytchMember, stytchMember.organization_id)) ||
                    (data.member && {
                        id: data.member.id,
                        email: data.member.email ?? undefined,
                        stytchOrganizationId: data.member.organization_id ?? undefined,
                        stytchMemberId: data.member.id,
                    }) ||
                    userProfile ||
                    null;
                setUserProfile(profile);
                if (!workspace && data.claims?.workspace_id) {
                    setWorkspace({
                        id: data.claims.workspace_id,
                        name: data.claims.workspace_name ?? '',
                    });
                }
                setClaims(data.claims ?? data.session?.claims ?? null);
                setError(null);
                // Removed cache manager usage - React Query handles all caching now
                return { success: true };
            }
            else {
                setUserProfile(null);
                setWorkspace(null);
                setClaims(null);
                return { success: false, reason: 'not_authenticated' };
            }
        }
        catch (e) {
            console.error('whoami error', e);
            setError(e);
            // Removed cache manager usage - React Query handles all caching now
            return { success: false, reason: 'error' };
        }
        finally {
            isWhoamiingRef.current = false;
        }
    }, [stytchMember, userProfile?.id, workspace]);
    const refresh = useCallback(async () => {
        if (!hasRefreshableSessionRef.current) {
            return { success: false, reason: 'not_authenticated' };
        }
        const now = Date.now();
        if (isRefreshingRef.current || now - lastRefreshRef.current < REFRESH_COOLDOWN_MS) {
            return { success: false, reason: 'rate_limited' };
        }
        isRefreshingRef.current = true;
        lastRefreshRef.current = now;
        try {
            const r = await fetch('/api/auth/refresh', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Cache-Control': 'no-store' },
            });
            if (!r.ok) {
                if (r.status === 401) {
                    setUserProfile(null);
                    setWorkspace(null);
                    setClaims(null);
                }
                throw new Error(`refresh failed: ${r.status}`);
            }
            const data = await r.json();
            if (data.session?.claims)
                setClaims(data.session.claims);
            refreshRetryRef.current = 0;
            return { success: true };
        }
        catch (e) {
            // Only log refresh errors in development
            if (process.env.NODE_ENV === 'development') {
                console.error('refresh error', e);
            }
            setError(e);
            // Retry up to 3 times with exponential backoff (30s, 60s, 120s)
            const attempt = refreshRetryRef.current;
            if (attempt < 3) {
                refreshRetryRef.current = attempt + 1;
                const delayMs = 30_000 * 2 ** attempt;
                clearRefreshTimer();
                refreshTimerRef.current = window.setTimeout(() => {
                    fireAndForget(refreshRef.current());
                }, delayMs);
            }
            return { success: false };
        }
        finally {
            isRefreshingRef.current = false;
        }
    }, [clearRefreshTimer]);
    // Keep refreshRef in sync with current refresh function
    useEffect(() => {
        refreshRef.current = refresh;
    }, [refresh]);
    const refreshAuth = useCallback(async () => {
        await refresh();
    }, [refresh]);
    useEffect(() => {
        if (!isInitialized)
            return;
        setIsLoading(false);
        const hasValidSession = hasFullSession || hasIntermediate || Boolean(userProfile) || hasE2EBypass || hasDevLogin;
        if (!hasValidSession)
            return;
        // whoami() hydrates client-side auth state (profile, claims, workspace).
        // JWT refresh is handled automatically by the Stytch SDK (every 3 min)
        // and validated by the proxy on every request — no client-side refresh needed.
        fireAndForget(whoami());
    }, [isInitialized, whoami, hasFullSession, hasIntermediate, userProfile, hasE2EBypass, hasDevLogin]);
    useEffect(() => {
        if (!isInitialized)
            return;
        if (stytchMember) {
            const profile = convertMemberToUserProfile(stytchMember, stytchMember.organization_id);
            setUserProfile(profile);
            fireAndForget(whoami());
        }
    }, [isInitialized, stytchMember, whoami]);
    useEffect(() => () => clearRefreshTimer(), [clearRefreshTimer]);
    const logout = useCallback(async () => {
        try {
            clearIntermediateSession();
            clearRefreshTimer();
            setUserProfile(null);
            setWorkspace(null);
            setClaims(null);
            setError(null);
            // Removed cache manager usage - React Query handles all caching now
        }
        catch (e) {
            console.error('Logout error:', e);
        }
    }, [clearRefreshTimer]);
    function hasPermissionSafeFromClaims(c, perm) {
        const perms = c.permissions ?? c.perms ?? [];
        return perms.includes(perm);
    }
    function hasRoleSafeFromClaims(c, role) {
        const roles = c.roles ?? [];
        return roles.includes(role);
    }
    const hasPermission = useCallback((perm) => (claims && userProfile?.id ? hasPermissionSafeFromClaims(claims, perm) : false), [claims, userProfile]);
    const hasRole = useCallback((role) => (claims && userProfile?.id ? hasRoleSafeFromClaims(claims, role) : false), [claims, userProfile]);
    const contextValue = useMemo(() => ({
        isAuthenticated,
        userProfile,
        workspace,
        // Stytch SDK types may not perfectly match our internal types
        stytchMember: stytchMember,
        stytchOrganization: stytchOrganization,
        isLoading,
        error,
        jwt: null,
        refreshAuth,
        logout,
        hasPermission,
        hasRole,
        updateAuthState: ({ userProfile: nu, workspace: nw }) => {
            if (nu !== undefined)
                setUserProfile(nu);
            if (nw !== undefined)
                setWorkspace(nw);
        },
        clearAuthState: () => {
            clearRefreshTimer();
            setUserProfile(null);
            setWorkspace(null);
            setClaims(null);
            setError(null);
        },
        setAuthError: (msg) => setError(msg ? new Error(msg) : null),
        session: stytchSession,
        checkSession: refreshAuth,
        login: async (email) => {
            console.log('login called with email:', email);
        },
    }), [
        isAuthenticated,
        userProfile,
        workspace,
        isLoading,
        error,
        stytchMember,
        stytchOrganization,
        refreshAuth,
        logout,
        hasPermission,
        hasRole,
        stytchSession,
        clearRefreshTimer,
    ]);
    return _jsx(AuthContext.Provider, { value: contextValue, children: children });
}
